import React from 'react';
import { dynastyTheme } from '../../services/colorService';
import { formatERA, formatBattingAvg } from '../../services/statFormatUtils';
import PlayerLink from '../../components/PlayerLink';

const COLS = [
  { key: 'ip', label: 'IP' },
  { key: 'h', label: 'H' },
  { key: 'er', label: 'ER', error: true },
  { key: 'bb', label: 'BB' },
  { key: 'so', label: 'SO', success: true },
  { key: 'hr', label: 'HR' },
  { key: 'era', label: 'ERA', rate: true, format: 'era' },
  { key: 'whip', label: 'WHIP', rate: true, format: 'whip' },
];

const DECISION_COLORS = {
  W: 'text-green-400',
  L: 'text-red-400',
  SV: 'text-blue-400',
  H: 'text-yellow-400',
  BS: 'text-red-400',
};

const PitchingLines = ({ pitching }) => {
  if (!pitching) return null;

  const players = pitching.players || [];
  const totals = pitching.totals || {};
  const team = pitching.team || '';

  return (
    <div className={`border ${dynastyTheme.classes.border.neutral} rounded-lg overflow-hidden`}>
      {/* Section header */}
      <div className={`px-4 py-2 ${dynastyTheme.classes.bg.darkLighter} border-b-2 border-yellow-500/40`}>
        <span className="text-sm font-bold text-white">{team} Pitching</span>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
            <th className={`py-2 px-3 text-left font-bold ${dynastyTheme.classes.text.white}`} style={{ minWidth: '220px' }}>Player</th>
            {COLS.map(c => (
              <th key={c.key} className={`py-2 px-2 text-center font-semibold ${dynastyTheme.classes.text.primary}`} style={{ minWidth: '40px' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p, idx) => {
            return (
              <tr key={p.player_id || idx}
                  className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-white/5`}
                  style={{ backgroundColor: p.ownership ? dynastyTheme.tokens.colors.primaryLight : (idx % 2 === 0 ? dynastyTheme.tokens.colors.neutral[900] : dynastyTheme.tokens.colors.neutral[800]) }}>
                <td className={`py-1.5 px-3 ${dynastyTheme.classes.text.white}`}>
                  <div className="flex items-center gap-2">
                    <PlayerLink mlbId={p.player_id} name={p.name} style={{ fontWeight: 500 }} />
                    {p.decision && (
                      <span className={`text-[10px] font-bold ${DECISION_COLORS[p.decision] || dynastyTheme.classes.text.neutralLight}`}>
                        ({p.decision})
                      </span>
                    )}
                    {p.ownership && (
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-semibold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark} whitespace-nowrap`}>
                        {p.ownership.fantasy_team}
                      </span>
                    )}
                  </div>
                </td>
                {COLS.map(c => {
                  const val = p[c.key];
                  let colorCls = dynastyTheme.classes.text.white;
                  if (c.rate) colorCls = dynastyTheme.classes.text.neutralLight;
                  else if (c.error && val > 0) colorCls = dynastyTheme.classes.text.error;
                  else if (c.success && val > 0) colorCls = dynastyTheme.classes.text.success;
                  return (
                    <td key={c.key} className={`py-1.5 px-2 text-center ${colorCls}`}>
                      {c.format === 'whip' ? formatBattingAvg(val) : c.rate ? formatERA(val) : (val ?? (c.key === 'ip' ? '0.0' : 0))}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Totals row with strong top border */}
          {players.length > 0 && (
            <tr className="border-t-2 border-yellow-500/40" style={{ backgroundColor: dynastyTheme.tokens.colors.primaryLight }}>
              <td className={`py-2 px-3 font-bold ${dynastyTheme.classes.text.primary}`}>TOTALS</td>
              {COLS.map(c => (
                <td key={c.key} className={`py-2 px-2 text-center font-bold ${dynastyTheme.classes.text.white}`}>
                  {c.format === 'whip' ? formatBattingAvg(totals[c.key]) : c.rate ? formatERA(totals[c.key]) : (totals[c.key] ?? 0)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>

      {pitching.pitching_notes?.length > 0 && (
        <div style={{ padding: '6px 12px', borderTop: '1px solid #2d2d2d', fontSize: 10, color: '#9ca3af' }}>
          {pitching.pitching_notes.join(' · ')}
        </div>
      )}
    </div>
  );
};

export default PitchingLines;
